import { PAISES_CON_RAIL } from '../../../utils/beneficiario-internacional.util';
import {
  DestinoRemesa,
  PerfilUsuario,
} from '../../remittance-hub/models/remittance.model';
import { FIAT_MIN_CLP } from '../../remittance-hub/rules/remittance-rules';
import {
  CorredorDef,
  EscalonVista,
  GrupoCorredor,
  MonedaMonto,
  ProductoTasa,
  TasaHome,
  TipoAhorro,
  TramoHome,
  TramoUsdtHome,
} from '../models/home-rates.model';
import { precioCorteValido } from '../../../../shared/utils/monto-usdt-tramo';

/**
 * Reglas puras de la tarjeta de tasas del home.
 *
 * Sin Angular y sin HTTP: qué corredores le tocan a cada perfil, cómo se
 * rotula cada uno y cuándo un dato dejó de ser confiable.
 *
 * El criterio que ordena todo lo de acá: **una fila, una línea** — salvo el
 * riel propio, que además muestra sus tres tramos en recuadros, porque ahí el
 * monto cambia la tasa y mostrar uno solo sería mostrarla a medias.
 */

/** Cada cuánto se refresca la tarjeta. */
export const CICLO_MS = 180_000;

/**
 * A partir de cuándo el valor se muestra apagado. Son dos ciclos: un fallo
 * aislado no ensucia la pantalla, dos seguidos sí hay que dejarlo ver.
 */
export const VENCE_MS = CICLO_MS * 2;

const CLAVES_TRAMOS: readonly string[] = ['rangoA', 'rangoB', 'rangoC'];

const FLAG_VE = 'assets/flags/venezuela.png';
const FLAG_CL = 'assets/flags/chile.png';
const FLAG_CO = 'assets/flags/colombia.png';
const FLAG_PE = 'assets/flags/peru.png';
const ICONO_USDT = 'assets/icons/USDT_Logo.png';

// ───────────────────────── Corredores por perfil ─────────────────────────

/**
 * Tres filas de nivel 1 por perfil, y cuatro destinos del proveedor.
 *
 * El nivel 1 es lo que el cliente mira todos los días: su riel propio y las
 * conversiones USDT. Los otros destinos van con las dos monedas de pago —cada
 * uno es un rail distinto— y se distinguen por el par de banderas y por el
 * nombre («Colombia» vs «Colombia con USDT»).
 */
const CORREDORES_CL: CorredorDef[] = [
  {
    id: 'CL:CLP>VE',
    nivel: 1,
    origen: 'CLP',
    destino: 'VE',
    fuente: 'TRAMOS_CLP_VES',
    // Más bolívares por peso es mejor para quien envía desde Chile.
    mejorEs: 'MAYOR',
    nombre: 'Venezuela',
    iconoOrigen: FLAG_CL,
    iconoDestino: FLAG_VE,
    grupo: 'ENVIO',
    base: 1,
    porLabel: 'por 1 CLP',
    simbolo: 'Bs.',
    decimales: '1.4-4',
    monedaMonto: 'CLP',
    tipoAhorro: 'RECIBE_MAS',
    montosTipicos: [50_000, 150_000, 500_000],
  },
  {
    id: 'CL:USDT>VE',
    nivel: 1,
    origen: 'USDT',
    destino: 'VE',
    fuente: 'COTIZACION',
    nombre: 'Venezuela con USDT',
    iconoOrigen: ICONO_USDT,
    iconoDestino: FLAG_VE,
    grupo: 'ENVIO',
    base: 1,
    porLabel: 'por 1 USDT',
    simbolo: 'Bs.',
    decimales: '1.2-2',
  },
  {
    id: 'CL:USDT',
    nivel: 1,
    origen: 'USDT',
    destino: null,
    fuente: 'USDT_COMPRA',
    fuenteVenta: 'USDT_VENTA',
    nombre: 'USDT',
    iconoOrigen: ICONO_USDT,
    iconoDestino: FLAG_CL,
    grupo: 'USDT',
    base: 1,
    porLabel: 'por 1 USDT',
    simbolo: 'CLP',
    decimales: '1.2-2',
    ruta: '/wallet/client/send-cripto',
    // Lado compra (CLP→USDT) y lado venta (USDT→CLP): sólo se usan si el
    // par llega con tramos y se parte en dos productos.
    monedaMonto: 'CLP',
    tipoAhorro: 'PAGA_MENOS',
    montosTipicos: [100_000, 500_000, 2_000_000],
    ladoVenta: { monedaMonto: 'USDT', tipoAhorro: 'RECIBE_MAS', montosTipicos: [100, 1_000, 5_000] },
  },
  {
    id: 'CL:CLP>CO',
    nivel: 2,
    origen: 'CLP',
    destino: 'CO',
    fuente: 'COTIZACION',
    nombre: 'Colombia',
    iconoOrigen: FLAG_CL,
    iconoDestino: FLAG_CO,
    grupo: 'ENVIO',
    base: 1,
    porLabel: 'por 1 CLP',
    simbolo: 'COP',
    decimales: '1.2-2',
  },
  {
    id: 'CL:CLP>PE',
    nivel: 2,
    origen: 'CLP',
    destino: 'PE',
    fuente: 'COTIZACION',
    nombre: 'Perú',
    iconoOrigen: FLAG_CL,
    iconoDestino: FLAG_PE,
    grupo: 'ENVIO',
    // Por unidad la tasa es ~0,0039: ilegible. Se expresa por mil.
    base: 1000,
    porLabel: 'por 1.000 CLP',
    simbolo: 'PEN',
    decimales: '1.2-2',
  },
  {
    id: 'CL:USDT>CO',
    nivel: 2,
    origen: 'USDT',
    destino: 'CO',
    fuente: 'COTIZACION',
    nombre: 'Colombia con USDT',
    iconoOrigen: ICONO_USDT,
    iconoDestino: FLAG_CO,
    grupo: 'ENVIO',
    base: 1,
    porLabel: 'por 1 USDT',
    simbolo: 'COP',
    decimales: '1.0-0',
  },
  {
    id: 'CL:USDT>PE',
    nivel: 2,
    origen: 'USDT',
    destino: 'PE',
    fuente: 'COTIZACION',
    nombre: 'Perú con USDT',
    iconoOrigen: ICONO_USDT,
    iconoDestino: FLAG_PE,
    grupo: 'ENVIO',
    base: 1,
    porLabel: 'por 1 USDT',
    simbolo: 'PEN',
    decimales: '1.2-2',
  },
];

const CORREDORES_VE: CorredorDef[] = [
  {
    id: 'VE:VES>CL',
    nivel: 1,
    origen: 'VES',
    destino: 'CL',
    fuente: 'TRAMOS_VES_CLP',
    // Acá la tasa también se publica como bolívares por peso, pero el cliente
    // los PAGA: menos bolívares por peso es mejor. Es al revés que arriba.
    mejorEs: 'MENOR',
    nombre: 'Chile',
    iconoOrigen: FLAG_VE,
    iconoDestino: FLAG_CL,
    grupo: 'ENVIO',
    // El riel publica VES por 1 CLP y así se muestra, igual que la pantalla
    // vigente (`1CLP=3,2800VES`). No se invierte: «1 Bs. = 0,29 CLP» no le
    // sirve a nadie y los tramos vienen expresados en CLP de todos modos.
    base: 1,
    porLabel: 'por 1 CLP',
    simbolo: 'Bs.',
    decimales: '1.4-4',
    monedaMonto: 'VES',
    tipoAhorro: 'PAGA_MENOS',
    montosTipicos: [50_000, 200_000, 1_000_000],
  },
  {
    id: 'VE:USDT>CL',
    nivel: 1,
    origen: 'USDT',
    destino: 'CL',
    fuente: 'COTIZACION',
    nombre: 'Chile con USDT',
    iconoOrigen: ICONO_USDT,
    iconoDestino: FLAG_CL,
    grupo: 'ENVIO',
    base: 1,
    porLabel: 'por 1 USDT',
    simbolo: 'CLP',
    decimales: '1.2-2',
  },
  {
    id: 'VE:USDT',
    nivel: 1,
    origen: 'USDT',
    destino: null,
    fuente: 'USDT_COMPRA_VES',
    fuenteVenta: 'USDT_VENTA_VES',
    nombre: 'USDT',
    iconoOrigen: ICONO_USDT,
    iconoDestino: FLAG_VE,
    grupo: 'USDT',
    base: 1,
    porLabel: 'por 1 USDT',
    simbolo: 'Bs.',
    decimales: '1.2-2',
    monedaMonto: 'VES',
    tipoAhorro: 'PAGA_MENOS',
    montosTipicos: [100_000, 600_000, 3_000_000],
    ladoVenta: { monedaMonto: 'USDT', tipoAhorro: 'RECIBE_MAS', montosTipicos: [50, 500, 2_000] },
  },
  {
    id: 'VE:VES>CO',
    nivel: 2,
    origen: 'VES',
    destino: 'CO',
    fuente: 'COTIZACION',
    nombre: 'Colombia',
    iconoOrigen: FLAG_VE,
    iconoDestino: FLAG_CO,
    grupo: 'ENVIO',
    base: 1,
    porLabel: 'por 1 Bs.',
    simbolo: 'COP',
    decimales: '1.2-2',
  },
  {
    id: 'VE:VES>PE',
    nivel: 2,
    origen: 'VES',
    destino: 'PE',
    fuente: 'COTIZACION',
    nombre: 'Perú',
    iconoOrigen: FLAG_VE,
    iconoDestino: FLAG_PE,
    grupo: 'ENVIO',
    base: 1000,
    porLabel: 'por 1.000 Bs.',
    simbolo: 'PEN',
    decimales: '1.2-2',
  },
  {
    id: 'VE:USDT>CO',
    nivel: 2,
    origen: 'USDT',
    destino: 'CO',
    fuente: 'COTIZACION',
    nombre: 'Colombia con USDT',
    iconoOrigen: ICONO_USDT,
    iconoDestino: FLAG_CO,
    grupo: 'ENVIO',
    base: 1,
    porLabel: 'por 1 USDT',
    simbolo: 'COP',
    decimales: '1.0-0',
  },
  {
    id: 'VE:USDT>PE',
    nivel: 2,
    origen: 'USDT',
    destino: 'PE',
    fuente: 'COTIZACION',
    nombre: 'Perú con USDT',
    iconoOrigen: ICONO_USDT,
    iconoDestino: FLAG_PE,
    grupo: 'ENVIO',
    base: 1,
    porLabel: 'por 1 USDT',
    simbolo: 'PEN',
    decimales: '1.2-2',
  },
];

/**
 * Corredores del perfil.
 *
 * `soloPublicos` deja únicamente los que salen de un endpoint sin
 * autenticación: es lo que ve el cliente que todavía no completó la
 * validación, que no puede operar pero sí merece saber a cuánto está la tasa.
 */
export function corredoresDe(
  perfil: PerfilUsuario,
  soloPublicos = false,
): CorredorDef[] {
  const todos = perfil === 'VE' ? CORREDORES_VE : CORREDORES_CL;
  return soloPublicos ? todos.filter(esPublico) : todos;
}

/** El corredor se resuelve con un endpoint que no pide token. */
export function esPublico(def: CorredorDef): boolean {
  return def.fuente === 'TRAMOS_CLP_VES' || def.fuente === 'TRAMOS_VES_CLP';
}

export function deNivel(defs: CorredorDef[], nivel: 1 | 2): CorredorDef[] {
  return defs.filter((d) => d.nivel === nivel);
}

/**
 * Encabezado de cada grupo. Mezclar «Venezuela» con «Comprar» en una lista
 * corrida era lo que hacía ilegible la tarjeta: son dos cosas distintas y el
 * cliente las busca por separado.
 */
export const TITULO_GRUPO: Record<GrupoCorredor, string> = {
  ENVIO: 'Enviar dinero',
  USDT: 'USDT',
};

/**
 * ¿El destino tiene riel de envío?
 *
 * `listCountries()` devuelve los países activos DEL PROVEEDOR, que no son los
 * mismos a los que se puede enviar: eso lo dice `PAISES_CON_RAIL`. Sin este
 * cruce la tarjeta puede mostrar un país con tasa y sin forma de mandarle
 * plata.
 */
export function tieneRiel(destino: DestinoRemesa | null): boolean {
  if (!destino) return true; // las conversiones no dependen del proveedor
  if (destino === 'VE' || destino === 'CL') return true; // riel propio
  return PAISES_CON_RAIL.indexOf(destino) >= 0;
}

// ───────────────────────── Tramos ─────────────────────────

/**
 * Pisos de los tres tramos, en CLP. Se leen de la respuesta cuando vienen y se
 * cae a estas constantes cuando no — son las que hoy tiene configurado el riel
 * y las que muestra el home del operador.
 */
export const PISOS_TRAMOS_CLP: readonly number[] = [
  FIAT_MIN_CLP,
  100_000,
  210_000,
];

export interface TramoCrudo {
  pisoClp: number;
  tasa: number;
}

/**
 * Normaliza `ultimas-agrupadas` (las dos respuestas, CLP→VES y VES→CLP,
 * comparten forma) a los tres tramos en orden.
 *
 * Devuelve `null` si no hay ninguno con tasa: media respuesta vacía es un dato
 * roto, no un dato parcial.
 */
export function tramosDesde(data: any): TramoCrudo[] | null {
  if (!data) return null;

  // Franjas dinámicas: si el backend manda `tramos` (todas las franjas activas
  // ordenadas por `desde`), se pintan todas. Si no llega o viene vacío, se
  // sigue exactamente con los tres rangos fijos de siempre.
  const dinamicos = tramosDinamicosDesde(data);
  if (dinamicos) return dinamicos;

  const tramos: TramoCrudo[] = [];
  CLAVES_TRAMOS.forEach((clave, i) => {
    const crudo = data?.[clave];
    const tasa = Number(crudo?.client?.tasa);
    if (tasa > 0) {
      tramos.push({ pisoClp: pisoDe(crudo, PISOS_TRAMOS_CLP[i]), tasa });
    }
  });

  return tramos.length ? tramos : null;
}

/**
 * Lee `data.tramos` (franjas dinámicas). `null` si no viene, viene vacío o
 * ninguna franja trae tasa: en esos casos manda la forma vieja.
 */
export function tramosDinamicosDesde(data: any): TramoCrudo[] | null {
  const lista = data?.tramos;
  if (!Array.isArray(lista) || !lista.length) return null;

  const tramos: TramoCrudo[] = [];
  lista.forEach((crudo: any, i: number) => {
    const tasa = Number(crudo?.client?.tasa);
    if (!(tasa > 0)) return;
    const desde = Number(crudo?.desde);
    const porDefecto = PISOS_TRAMOS_CLP[Math.min(i, PISOS_TRAMOS_CLP.length - 1)];
    tramos.push({
      pisoClp: Number.isFinite(desde) && desde > 0 ? desde : porDefecto,
      tasa,
    });
  });
  tramos.sort((a, b) => a.pisoClp - b.pisoClp);
  return tramos.length ? tramos : null;
}

/** Piso que declara el backend, si lo declara. Si no, el que ya conocemos. */
function pisoDe(crudo: any, porDefecto: number): number {
  const texto = String(crudo?.monto ?? crudo?.rango ?? '');
  // SÓLO el primer número: el piso. Un rango como "$15.000 - $99.999" pasaba
  // antes por `replace(/[^0-9]/g,'')` sobre todo el texto y concatenaba piso y
  // techo en un número absurdo (150009999). Tomamos el primer grupo de dígitos.
  const primero = texto.match(/\d[\d.,]*/);
  const n = primero ? Number(primero[0].replace(/[^0-9]/g, '').slice(0, 9)) : 0;
  return n > 0 ? n : porDefecto;
}

/** La tasa que representa al corredor: la del primer tramo. */
export function tasaDeTramos(tramos: TramoCrudo[] | null): number | null {
  return tramos && tramos.length ? tramos[0].tasa : null;
}

/**
 * Arma los tramos que se pintan: piso en la moneda que paga el cliente y la
 * marca del mejor.
 *
 * Los pisos se convierten TODOS con la misma tasa —la del primer tramo— y no
 * cada uno con la suya. Convirtiendo con la propia, las bandas se solapan: con
 * 1,0313 y 1,0243, el techo del tramo A (103.129 Bs.) queda por encima del
 * piso del B (102.430 Bs.). Por eso el rótulo avisa que son aproximados y el
 * monto exacto lo confirma el cotizador.
 */
export function tramosParaVista(
  def: CorredorDef,
  tramos: TramoCrudo[] | null,
): TramoHome[] | null {
  if (!tramos || !tramos.length) return null;

  const base = tramos[0].tasa;
  const mejor = indiceDelMejor(tramos, def.mejorEs);

  return tramos.map((t, i) => {
    // `Math.ceil` directo sobre `pisoClp * base` disparaba off-by-one por ruido
    // de punto flotante (10000 * 1.0313 = 10313.0000000002 → 10314). Se redondea
    // a 6 decimales antes de techar: mata el ruido sin perder redondeos reales.
    const pisoVes = Math.ceil(Math.round(t.pisoClp * base * 1e6) / 1e6);
    return {
      desde: pisosAproximados(def)
        ? `${formatearMonto(pisoVes, 'VES')}+`
        : `${formatearMonto(t.pisoClp, 'CLP')}+`,
      tasa: t.tasa,
      mejor: i === mejor,
    };
  });
}

/** Índice del tramo que más le conviene al cliente. Sin regla, ninguno. */
export function indiceDelMejor(
  tramos: { tasa: number }[],
  mejorEs: 'MAYOR' | 'MENOR' | undefined,
): number {
  if (!mejorEs || !tramos.length) return -1;

  let mejor = 0;
  tramos.forEach((t, i) => {
    const gana =
      mejorEs === 'MAYOR'
        ? t.tasa > tramos[mejor].tasa
        : t.tasa < tramos[mejor].tasa;
    if (gana) mejor = i;
  });
  return mejor;
}

/**
 * Rótulo de la fila cuando los números viven en los recuadros: la unidad, y el
 * aviso de que depende del monto.
 */
export function etiquetaUnidad(def: CorredorDef): string {
  return `${def.simbolo} ${def.porLabel}, según cuánto envíes`;
}

/** La fila muestra dos precios —compra y venta— en vez de uno. */
export function esPar(def: CorredorDef): boolean {
  return !!def.fuenteVenta;
}

/** Los pisos del cliente VE salen de una conversión: hay que decirlo. */
export function pisosAproximados(def: CorredorDef): boolean {
  return def.origen === 'VES';
}

// ───────────────────────── Tramos VES→USDT ─────────────────────────

/** Un tramo de `rates/tramos-usdt/ves-usdt` tal como se guarda entre ciclos. */
export interface TramoUsdtCrudo {
  desde: number;
  hasta: number;
  tasa: number;
}

/** La fila que suma los tramos VES→USDT: el par USDT del cliente VE. */
export function llevaTramosUsdt(def: CorredorDef): boolean {
  return def.fuente === 'USDT_COMPRA_VES';
}

/**
 * Normaliza la respuesta del GET de tramos del cliente: con tasa válida y
 * ordenados por «desde». `null` si no queda ninguno.
 */
export function tramosUsdtDesde(res: any): TramoUsdtCrudo[] | null {
  const lista = Array.isArray(res?.tramos) ? res.tramos : [];
  const tramos: TramoUsdtCrudo[] = lista
    .map((t: any) => ({ desde: Number(t?.desde), hasta: Number(t?.hasta), tasa: Number(t?.tasa) }))
    .filter((t: TramoUsdtCrudo) => t.tasa > 0 && Number.isFinite(t.desde) && Number.isFinite(t.hasta))
    .sort((a: TramoUsdtCrudo, b: TramoUsdtCrudo) => a.desde - b.desde);
  return tramos.length ? tramos : null;
}

/**
 * Los recuadros de los tramos VES→USDT. `null` (se pinta como antes) con
 * menos de 2 tramos o si todos cobran lo mismo: no hay descuento que mostrar.
 * El mejor es el de tasa MENOR (paga menos bolívares por USDT).
 */
export function tramosUsdtParaVista(
  tramos: TramoUsdtCrudo[] | null | undefined,
  mejorEs: 'MAYOR' | 'MENOR' = 'MENOR',
): TramoUsdtHome[] | null {
  if (!tramos || tramos.length < 2) return null;
  const primera = tramos[0].tasa;
  if (!tramos.some((t) => Math.abs(t.tasa - primera) > 0.005)) return null;

  const mejor = indiceDelMejor(tramos, mejorEs);
  const n = (v: number) => new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(v);
  return tramos.map((t, i) => ({
    rotulo:
      i === 0
        ? `Hasta ${n(t.hasta)} USDT`
        : i === tramos.length - 1
          ? `Desde ${n(t.desde)} USDT` // mismo corte que el back (`>= desde`)
          : `${n(t.desde)} a ${n(t.hasta)} USDT`,
    tasa: t.tasa,
    mejor: i === mejor,
  }));
}

// ───────────────────────── Tramos USDT→VES (espejo) ─────────────────────────

/**
 * La fila que suma los tramos USDT→VES: el mismo par USDT del cliente VE (su
 * precio de venta es `USDT_VENTA_VES`).
 */
export function llevaTramosUsdtVenta(def: CorredorDef): boolean {
  return def.fuenteVenta === 'USDT_VENTA_VES';
}

/**
 * Los recuadros de los tramos USDT→VES. Mismas reglas que VES→USDT, pero el
 * mejor es el de tasa MAYOR (recibe más bolívares por USDT).
 */
export function tramosUsdtVentaParaVista(tramos: TramoUsdtCrudo[] | null | undefined): TramoUsdtHome[] | null {
  return tramosUsdtParaVista(tramos, 'MAYOR');
}

// ───────────────────────── Tramos CLP↔USDT (cliente CL) ─────────────────────────

/** La fila que suma los tramos CLP→USDT y USDT→CLP: el par USDT del cliente CL. */
export function llevaTramosClpUsdt(def: CorredorDef): boolean {
  return def.fuente === 'USDT_COMPRA';
}

/** `base` de `rates/tramos-usdt/clp-usdt`: con ella el back elige el tramo (`montoClp ÷ base`). */
export function baseDeTramos(res: any): number | null {
  const b = Number(res?.base);
  return b > 0 ? b : null;
}

/**
 * `precioCorte` de `rates/tramos-usdt/{ves-usdt|clp-usdt}` (23/10): el precio
 * del USDT con que el back pasa el monto a USDT para elegir el tramo (PROM P2P
 * o spot). `null` si no viene o no es válido: se elige como hoy.
 */
export function precioCorteDeTramos(res: any): number | null {
  return precioCorteValido(res?.precioCorte);
}

// ───────────────────────── Escalera (forma única) ─────────────────────────

/** Un tramo crudo cualquiera: piso en la unidad del corte y su tasa. */
export interface TramoCorte {
  desde: number;
  tasa: number;
}

/**
 * Dos tasas son distintas si difieren en más que el ruido de punto flotante,
 * RELATIVO a la tasa. Antes era 0,005 fijo: sirve para USDT (~1.000) pero en
 * Bs. por CLP (~1,03) es un 0,5 % y escondía franjas que suben de a 0,0001.
 */
export function tasasDistintas(a: number, b: number): boolean {
  return Math.abs(a - b) > Math.max(1e-9, Math.abs(b) * 1e-6);
}

export interface OpcionesEscalera {
  unidadCorte: 'CLP' | 'USDT';
  monedaMonto: MonedaMonto;
  tipoAhorro: TipoAhorro;
  /** Para rotular en Bs. un corte en CLP. Sin ella, la tasa del primer tramo. */
  base?: number | null;
  /**
   * Exige al menos 2 tramos con tasas distintas (los de USDT: la semilla de
   * prod es 1 tramo igual a la base y ahí no hay escalera que mostrar).
   */
  exigirMejora?: boolean;
}

/**
 * Unifica `tramosParaVista` y `tramosUsdtParaVista` en la forma única
 * `EscalonVista`, con un solo rótulo, «Desde X», que es el mismo corte que
 * aplica el back (`desde <= monto`). `null` si no hay escalera que mostrar.
 */
export function escaleraDesde(
  tramos: TramoCorte[] | null | undefined,
  op: OpcionesEscalera,
): EscalonVista[] | null {
  const lista = (tramos || [])
    .map((t) => ({ desde: Number(t?.desde), tasa: Number(t?.tasa) }))
    .filter((t) => t.tasa > 0 && Number.isFinite(t.desde))
    .sort((a, b) => a.desde - b.desde);
  if (!lista.length) return null;
  if (op.exigirMejora) {
    if (lista.length < 2) return null;
    if (!lista.some((t) => tasasDistintas(t.tasa, lista[0].tasa))) return null;
  }
  const base = op.base && op.base > 0 ? op.base : lista[0].tasa;
  const mejor = indiceDelMejor(lista, op.tipoAhorro === 'RECIBE_MAS' ? 'MAYOR' : 'MENOR');
  return lista.map((t, i) => ({
    desde: t.desde,
    unidadCorte: op.unidadCorte,
    rotulo: rotuloCorte(t.desde, op.unidadCorte, op.monedaMonto, base),
    tasa: t.tasa,
    mejor: i === mejor,
  }));
}

/**
 * «Desde 1.000 USDT», «Desde $210.000» o, si el cliente paga en Bs., «Desde ≈ Bs. 69.300 ($210.000)».
 * `locale`: el de la pantalla (es-CL por defecto; las calculadoras, con el
 * campo en en-US, pasan `'en-US'` para no mezclar «2,000» con «2.501»).
 */
export function rotuloCorte(
  desde: number,
  unidadCorte: 'CLP' | 'USDT',
  monedaMonto: MonedaMonto,
  base: number,
  locale: LocaleMontos = 'es-CL',
): string {
  if (unidadCorte === 'USDT') return `Desde ${entero(desde, locale)} USDT`;
  if (monedaMonto === 'VES') {
    return `Desde ≈ ${formatearDinero(techoSinRuido(desde * base), 'VES', locale)} (${formatearDinero(desde, 'CLP', locale)})`;
  }
  return `Desde ${formatearDinero(desde, 'CLP', locale)}`;
}

/** El corte sin rótulo largo, para los ejes de la barra: «1.000 USDT», «$5.000», «≈ Bs. 16.500». */
export function corteCorto(
  e: EscalonVista,
  monedaMonto: MonedaMonto,
  base: number,
  locale: LocaleMontos = 'es-CL',
): string {
  if (e.unidadCorte === 'USDT') return `${entero(e.desde, locale)} USDT`;
  if (monedaMonto === 'VES') return `≈ ${formatearDinero(techoSinRuido(e.desde * base), 'VES', locale)}`;
  return formatearDinero(e.desde, 'CLP', locale);
}

/** ¿El monto se escribe en otra moneda que el corte? Entonces los pisos son aproximados. */
export function montoAproximado(e: EscalonVista[] | null, monedaMonto: MonedaMonto): boolean {
  return !!e && !!e.length && e[0].unidadCorte !== monedaMonto;
}

/** Monto escrito → unidad del corte (`montoClp ÷ base` en la compra, como el back). */
export function aCorte(monto: number, monedaMonto: MonedaMonto, unidadCorte: 'CLP' | 'USDT', base: number): number {
  if (monedaMonto === unidadCorte) return monto;
  return base > 0 ? monto / base : 0;
}

/** Unidad del corte → moneda del monto. */
export function deCorte(corte: number, monedaMonto: MonedaMonto, unidadCorte: 'CLP' | 'USDT', base: number): number {
  return monedaMonto === unidadCorte ? corte : corte * base;
}

/**
 * Índice del tramo que le toca al monto: el ÚLTIMO con `desde <= monto` (en la
 * unidad del corte) y el primero por defecto. Es el mismo corte del back, SIN
 * tolerancia: con base 900,19 y corte 1.000 USDT, 900.190 CLP dan
 * 999,9999999999999 USDT y el servidor los deja en el tramo anterior; acá
 * también. El monto que sí entra lo calcula `montoParaCorte`.
 * `-1` sólo con la escalera vacía.
 */
export function tramoDelMonto(
  escalera: EscalonVista[] | null | undefined,
  monto: number,
  base: number | null | undefined,
  monedaMonto: MonedaMonto,
): number {
  if (!escalera || !escalera.length) return -1;
  const b = base && base > 0 ? base : escalera[0].tasa;
  const m = Number(monto) > 0 ? Number(monto) : 0;
  const c = aCorte(m, monedaMonto, escalera[0].unidadCorte, b);
  let i = 0;
  escalera.forEach((e, k) => {
    if (e.desde <= c) i = k;
  });
  return i;
}

/** Paso del campo de monto: pesos enteros; Bs. y USDT al centavo. */
export function pasoDeMonto(monedaMonto: MonedaMonto | string): number {
  return monedaMonto === 'CLP' ? 1 : 0.01;
}

/**
 * El MENOR monto, en pasos del campo (1 peso; 1 centavo en Bs. y USDT), que
 * cae en el tramo que arranca en `desde` según el servidor (`aCorte(monto) >=
 * desde`, exacto). Parte del techo de la conversión y sube de a un paso
 * mientras la división quede un pelo por debajo del corte (900.190 ÷ 900,19 =
 * 999,9999999999999 → 900.191).
 */
export function montoParaCorte(
  desde: number,
  monedaMonto: MonedaMonto,
  unidadCorte: 'CLP' | 'USDT',
  base: number,
): number {
  const paso = pasoDeMonto(monedaMonto);
  const deN = (n: number): number => (paso === 1 ? n : n / 100);
  const entra = (n: number): boolean => aCorte(deN(n), monedaMonto, unidadCorte, base) >= desde;
  const exacto = deCorte(desde, monedaMonto, unidadCorte, base) / paso;
  if (!Number.isFinite(exacto) || exacto <= 0) return 0;
  let n = Math.ceil(Math.round(exacto * 1e6) / 1e6);
  // Ajuste fino de a un paso: es ruido de la división, a lo sumo un par de vueltas.
  for (let k = 0; k < 1000 && n > 0 && entra(n - 1); k++) n--;
  for (let k = 0; k < 1000 && !entra(n); k++) n++;
  return deN(n);
}

/** Ahorro (o «recibes más») frente a la tasa base, en dinero. */
export interface Ahorro {
  valor: number;
  /** Moneda en la que se cuenta. */
  moneda: MonedaMonto;
  tipo: TipoAhorro;
}

/**
 * Cuánto gana el cliente con `tasa` frente a la base (la del primer tramo).
 *
 *  - `RECIBE_MAS` (la tasa es lo que recibe por unidad pagada): `monto × (tasa − base)`, en la moneda de destino.
 *  - `PAGA_MENOS` (la tasa es lo que paga por unidad recibida): recibe `monto ÷ tasa` y ahorra `recibe × (base − tasa)`, en la moneda del monto.
 *
 * Nunca negativo: un tramo peor que la base no se vende como ahorro.
 */
export function ahorroFrenteABase(
  monto: number,
  tasa: number,
  base: number,
  tipoAhorro: TipoAhorro,
  monedaMonto: MonedaMonto,
  monedaDestino: MonedaMonto,
): Ahorro {
  const m = Number(monto) > 0 ? Number(monto) : 0;
  if (!(tasa > 0) || !(base > 0) || !m) {
    return { valor: 0, moneda: tipoAhorro === 'RECIBE_MAS' ? monedaDestino : monedaMonto, tipo: tipoAhorro };
  }
  if (tipoAhorro === 'RECIBE_MAS') {
    return { valor: Math.max(0, m * (tasa - base)), moneda: monedaDestino, tipo: tipoAhorro };
  }
  const recibe = m / tasa;
  return { valor: Math.max(0, recibe * (base - tasa)), moneda: monedaMonto, tipo: tipoAhorro };
}

export interface FaltaSiguiente {
  /** Índice del tramo siguiente. */
  indice: number;
  siguiente: EscalonVista;
  /**
   * Cuánto falta, en la moneda del monto: `montoSiguiente − monto`, hacia
   * arriba al paso del campo. Siempre > 0 (si no, ya está en el tramo).
   */
  falta: number;
  /**
   * El menor monto que cae en el tramo siguiente según el servidor, en pasos
   * del campo (`montoParaCorte`). Es el que escribe «Usar X».
   */
  montoSiguiente: number;
  /** El corte está en otra moneda que el monto: la cifra es aproximada. */
  aproximado: boolean;
}

/** Lo que le falta al monto para el tramo siguiente. `null` si ya está en el último. */
export function faltaParaSiguiente(
  escalera: EscalonVista[] | null | undefined,
  monto: number,
  base: number | null | undefined,
  monedaMonto: MonedaMonto,
): FaltaSiguiente | null {
  const i = tramoDelMonto(escalera, monto, base, monedaMonto);
  if (!escalera || i < 0 || i >= escalera.length - 1) return null;
  const b = base && base > 0 ? base : escalera[0].tasa;
  const u = escalera[0].unidadCorte;
  const m = Number(monto) > 0 ? Number(monto) : 0;
  const siguiente = escalera[i + 1];
  const montoSiguiente = montoParaCorte(siguiente.desde, monedaMonto, u, b);
  // El mismo monto que escribe «Usar X», menos el actual, hacia arriba al paso
  // del campo: el texto y el botón nunca se contradicen y no hay «Te faltan $0».
  const paso = pasoDeMonto(monedaMonto);
  const pasos = Math.ceil(Math.round(((montoSiguiente - m) / paso) * 1e6) / 1e6);
  if (!(montoSiguiente > m) || !(pasos > 0)) return null;
  return {
    indice: i + 1,
    siguiente,
    falta: paso === 1 ? pasos : pasos / 100,
    montoSiguiente,
    aproximado: monedaMonto !== u,
  };
}

/** Alto de la barrita `i` de `n` de la mini escalera: proporcional, sirve con 3 o con 8 tramos (4 a 12 px). */
export function alturaMini(i: number, n: number): number {
  if (!(n > 0)) return 4;
  return Math.round(4 + (8 * (Math.min(Math.max(i, 0), n - 1) + 1)) / n);
}

/**
 * La tasa grande de la fila: la del PRIMER tramo, la que recibe la mayoría.
 * Mostrar la mejor prometía una tasa que solo aplica a montos grandes.
 */
export function tasaInicial(escalera: EscalonVista[] | null | undefined): number | null {
  return escalera && escalera.length ? escalera[0].tasa : null;
}

/**
 * «Mejor 1,0151 desde $4.991.001» / «Mejor 998,04 desde 5.001 USDT». Sirve en
 * los dos sentidos (paga menos o recibe más) porque dice cuál es la mejor y
 * desde qué monto. `null` si la mejor es la del primer tramo.
 */
export function rotuloMejor(
  escalera: EscalonVista[] | null | undefined,
  monedaMonto: MonedaMonto,
  base: number | null | undefined,
  decimales: string,
): string | null {
  if (!escalera || escalera.length < 2) return null;
  const i = escalera.findIndex((e) => e.mejor);
  if (i <= 0) return null;
  const b = base && base > 0 ? base : escalera[0].tasa;
  return `Mejor ${formatearTasa(escalera[i].tasa, decimales)} desde ${corteCorto(escalera[i], monedaMonto, b)}`;
}

/** La tasa a mostrar en «Desde X»: la del mejor escalón. */
export function mejorTasa(escalera: EscalonVista[] | null | undefined): number | null {
  if (!escalera || !escalera.length) return null;
  const m = escalera.find((e) => e.mejor) || escalera[escalera.length - 1];
  return m.tasa;
}

/**
 * Productos que dibuja la tarjeta para una fila. El par USDT se parte en
 * «Comprar USDT» y «Vender USDT» SOLO si llega alguna escalera; si no, queda
 * como hoy: una fila con «Compras a · Vendes a».
 */
export function productosDe(t: TasaHome): ProductoTasa[] {
  const def = t.def;
  const unidad = `${def.simbolo} ${def.porLabel}`;
  const comun = {
    fila: t,
    unidad,
    simbolo: def.simbolo,
    decimales: def.decimales,
  };

  if (esPar(def)) {
    if (!t.escalera && !t.escaleraVenta) {
      return [productoSimple(t, unidad)];
    }
    const local = def.monedaMonto || 'CLP';
    const venta = def.ladoVenta;
    return [
      {
        ...comun,
        id: `${def.id}:compra`,
        nombre: 'Comprar USDT',
        iconoOrigen: def.iconoDestino,
        iconoDestino: def.iconoOrigen,
        escalera: t.escalera ?? null,
        base: t.baseEscalera ?? null,
        precioCorte: t.precioCorteEscalera ?? null,
        tasa: t.tasa,
        monedaMonto: local,
        monedaDestino: 'USDT',
        tipoAhorro: def.tipoAhorro || 'PAGA_MENOS',
        montosTipicos: def.montosTipicos || [],
        verbo: '¿Cuánto vas a cambiar?',
        accion: 'Comprar USDT',
      },
      {
        ...comun,
        id: `${def.id}:venta`,
        nombre: 'Vender USDT',
        iconoOrigen: def.iconoOrigen,
        iconoDestino: def.iconoDestino,
        escalera: t.escaleraVenta ?? null,
        base: null,
        tasa: t.venta,
        monedaMonto: venta?.monedaMonto || 'USDT',
        monedaDestino: local,
        tipoAhorro: venta?.tipoAhorro || 'RECIBE_MAS',
        montosTipicos: venta?.montosTipicos || [],
        verbo: '¿Cuánto vas a vender?',
        accion: 'Vender USDT',
      },
    ];
  }

  if (t.escalera && t.escalera.length) {
    const monedaMonto = def.monedaMonto || 'CLP';
    return [
      {
        ...comun,
        id: def.id,
        nombre: def.nombre,
        iconoOrigen: def.iconoOrigen,
        iconoDestino: def.iconoDestino,
        escalera: t.escalera,
        base: t.baseEscalera ?? null,
        tasa: t.tasa,
        monedaMonto,
        monedaDestino: monedaDeDestino(def.destino),
        tipoAhorro: def.tipoAhorro || (def.mejorEs === 'MENOR' ? 'PAGA_MENOS' : 'RECIBE_MAS'),
        montosTipicos: def.montosTipicos || [],
        verbo: '¿Cuánto vas a enviar?',
        accion: `Enviar a ${def.nombre}`,
      },
    ];
  }

  return [productoSimple(t, unidad)];
}

function productoSimple(t: TasaHome, unidad: string): ProductoTasa {
  const def = t.def;
  return {
    fila: t,
    id: def.id,
    nombre: def.nombre,
    iconoOrigen: def.iconoOrigen,
    iconoDestino: def.iconoDestino,
    unidad,
    simbolo: def.simbolo,
    decimales: def.decimales,
    escalera: null,
    base: null,
    tasa: t.tasa,
    monedaMonto: def.monedaMonto || 'CLP',
    monedaDestino: monedaDeDestino(def.destino),
    tipoAhorro: def.tipoAhorro || 'RECIBE_MAS',
    montosTipicos: def.montosTipicos || [],
    verbo: '',
    accion: '',
  };
}

function monedaDeDestino(destino: DestinoRemesa | null): MonedaMonto {
  if (destino === 'VE') return 'VES';
  if (destino === 'CL') return 'CLP';
  return 'USDT';
}

/**
 * Formato de montos y tasas de una pantalla. Regla: dentro de cada pantalla,
 * un solo formato, el de su campo de monto (el home y la escalera por
 * defecto, es-CL; las 4 calculadoras, en-US como su campo).
 */
export type LocaleMontos = 'es-CL' | 'en-US';

/** Entero sin decimales: «1.000» (es-CL) o «1,000» (en-US). */
function entero(v: number, locale: LocaleMontos = 'es-CL'): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(v);
}

/** `Math.ceil` sin el ruido de punto flotante (10000 × 1,0313 = 10313,0000000002). */
function techoSinRuido(v: number): number {
  return Math.ceil(Math.round(v * 1e6) / 1e6);
}

/**
 * Dinero para los mensajes del simulador, es-CL: «$162», «Bs. 12.345»
 * (con 2 decimales bajo 100), «506,5 USDT».
 */
export function formatearDinero(
  valor: number,
  moneda: MonedaMonto | string,
  locale: LocaleMontos = 'es-CL',
): string {
  const v = Number(valor) || 0;
  const nf = (min: number, max: number) =>
    new Intl.NumberFormat(locale, { minimumFractionDigits: min, maximumFractionDigits: max });
  if (moneda === 'CLP') return `$${nf(0, 0).format(Math.round(v))}`;
  if (moneda === 'VES') return `Bs. ${Math.abs(v) >= 100 ? nf(0, 0).format(Math.round(v)) : nf(2, 2).format(v)}`;
  if (moneda === 'USDT') return `${nf(0, 2).format(v)} USDT`;
  return `${nf(2, 2).format(v)} ${moneda}`;
}

// ───────────────────────── Rótulos ─────────────────────────

/**
 * Tasa con formato es-CL («1.234,56»), igual que los rótulos de los tramos.
 * `digitos` es el digitsInfo de Angular de la fila (`'1.2-2'`). El pipe
 * `number` usa el LOCALE_ID de la app (en-US) y mezclaba «1,234.56» con el
 * «1.000» del rótulo.
 */
export function formatearTasa(
  valor: number | null | undefined,
  digitos = '1.2-2',
  locale: LocaleMontos = 'es-CL',
): string {
  const n = Number(valor);
  if (valor == null || !Number.isFinite(n)) return '—';
  const m = /^\d+\.(\d+)-(\d+)$/.exec(digitos || '');
  const min = m ? Number(m[1]) : 2;
  const max = m ? Number(m[2]) : 2;
  return new Intl.NumberFormat(locale, { minimumFractionDigits: min, maximumFractionDigits: max }).format(n);
}

export function formatearMonto(monto: number, moneda: string, locale: LocaleMontos = 'es-CL'): string {
  const n = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(
    monto,
  );
  if (moneda === 'CLP') return `$${n}`;
  if (moneda === 'VES') return `Bs. ${n}`;
  return `${n} ${moneda}`;
}

// ───────────────────────── Frescura ─────────────────────────

/**
 * El dato pasó dos ciclos sin renovarse. No se borra —una tasa vieja informa
 * más que un guión— pero se muestra apagada.
 */
export function estaVencida(
  actualizadaEn: number | null,
  ahora: number,
): boolean {
  if (actualizadaEn == null) return false;
  return ahora - actualizadaEn > VENCE_MS;
}
